import { FC, useContext, useState, useEffect } from "react";
import Configuration from "../../types/Configuration";
import {
  styles as commonStyles,
  sxStyles as commonSxStyles,
} from "./common-styles";
import {
  Box,
  Typography,
  Colors,
  Collapse,
  Table,
  tableCellClasses,
  tableRowClasses,
} from "@analyticsMD/quil";
import { css, cx } from "@emotion/css";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SectionGrid from "../section-grid";
import APIContext from "../../contexts/APIContext";
import { Icons, Tooltip, tooltipClasses } from "@analyticsMD/quil";
import { Button } from "@mui/material";
import PatientConsideration, {
  ConsiderationStatusChoices,
} from "../../types/PatientConsideration";
import moment from "moment";
import { PatientConsiderationObj } from "../../components/patient-consideration";
import { useMutation, useQueryClient } from "react-query";
import { patchPatientConsideration } from "../../api/endpoints";
import NotificationToast from "../notification-toast";
import { lightBlue } from "@mui/material/colors";

const ERROR_TOAST_MESSAGE =
  "There was an error submitting your feedback. Please try again.";

const styles = {
  ...commonStyles,
  considerationSection: css`
    background-color: ${Colors.white};
  `,
  tableRow: css`
    pointer-events: none;
  `,
};

const sxStyles = {
  ...commonSxStyles,
  valueWrapper: { display: "flex" },
  secondaryValue: {
    fontSize: ".75rem",
    textAlign: "right",
    color: "rgba(0, 0, 0, 0.6)",
    fontWeight: "400",
  },
  key: (tooltipEnabled: boolean) => ({
    fontSize: "0.831rem",
    fontWeight: "bold",
    color: Colors.grey_65,
    textDecoration: tooltipEnabled ? "underline" : "none",
    textDecorationStyle: tooltipEnabled ? "dashed" : "none",
    textUnderlineOffset: "0.2rem",
  }),
  table: {
    [`& .${tableRowClasses.root}`]: {
      [`& .${tableCellClasses.root}:first-of-type`]: {
        border: "none",
        padding: "0rem",
        width: "0rem",
      },
      [`& .${tableCellClasses.root}`]: {
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem",
        textAlign: "left",
        textAlignLast: "left",
        paddingLeft: "0.3rem",
        cursor: "pointer",
      },
    },
  },
  considerationBtn: {
    textTransform: "none",
    marginRight: "0.25rem",
  },
  acceptConsiderationBtn: {
    marginRight: "0rem",
  },
  considerationActionWrapper: {
    display: "flex",
    justifyContent: "end",
  },
  considerationTooltip: {
    fontSize: "0.8rem",
    bottom: "0.4rem",
    [`&.${tooltipClasses.tooltip}`]: {
      width: "80%",
      backgroundColor: Colors.grey_80,
      color: Colors.white,
      borderRadius: "0.2rem !important",
      boxShadow: "none",
      paddingTop: "0.4rem",
      paddingBottom: "0.4rem",
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: Colors.grey_80,
      top: "0.2rem !important",
    },
  },
};

interface ConsiderationSectionProps {
  configuration: Configuration;
}

const ConsiderationSection: FC<ConsiderationSectionProps> = ({
  configuration,
}) => {
  const [showSection, setShowSection] = useState(true);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const { patientConsiderationsQuery } = useContext(APIContext);
  const queryClient = useQueryClient();
  const considerationsData: PatientConsideration[] =
    patientConsiderationsQuery.isSuccess ? patientConsiderationsQuery.data : [];

  const patchMutation = useMutation(patchPatientConsideration, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["patientconsiderations"] });
    },
    onError: () => {
      setShowErrorToast(true);
    },
  });

  const handleAccept = (consideration: PatientConsideration) => {
    const payload = {
      id: consideration.id,
      status: ConsiderationStatusChoices.ACCEPTED,
    };
    patchMutation.mutate(payload);
  };

  const handleRemove = (consideration: PatientConsideration) => {
    const payload = {
      id: consideration.id,
      status: ConsiderationStatusChoices.REJECTED,
    };
    patchMutation.mutate(payload);
  };

  const columns = [
    {
      headerName: "",
      field: "",
      renderCell: (row) => <Box />,
    },
    {
      headerName: "",
      field: "consideration.display_name",
      renderCell: (row: PatientConsideration) => {
        const description = new PatientConsiderationObj(row).getDescription();
        return (
          <Box sx={sxStyles.valueWrapper}>
            <Tooltip
              title={description}
              placement="right"
              arrow
              disableHoverListener={!description}
              componentsProps={{
                tooltip: {
                  sx: sxStyles.considerationTooltip,
                },
              }}
            >
              <Box>
                <Typography sx={sxStyles.key(!!description)}>
                  {row.consideration.display_text}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      headerName: "",
      field: "considereation_actions",
      renderCell: (row: PatientConsideration) => {
        if (row.status === ConsiderationStatusChoices.ACCEPTED) {
          return (
            <Box sx={sxStyles.considerationActionWrapper}>
              <Typography sx={sxStyles.secondaryValue}>
                {row.updated_by_name || row.updated_by} accepted{" "}
                {moment(row.qv_updated_ts).fromNow()}
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={sxStyles.considerationActionWrapper}>
            <Button
              variant="text"
              size="small"
              onClick={() => handleRemove(row)}
              sx={sxStyles.considerationBtn}
            >
              Remove
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleAccept(row)}
              sx={{
                ...sxStyles.considerationBtn,
                ...sxStyles.acceptConsiderationBtn,
              }}
            >
              Accept
            </Button>
          </Box>
        );
      },
    },
  ];

  const renderConsiderationSection = () => {
    if (considerationsData.length > 0) {
      return (
        <Box sx={{ marginTop: "0.4rem" }}>
          <Table
            columns={columns}
            rows={considerationsData}
            sx={sxStyles.table}
            defaultSortColumn="display_name"
            hideHeader
            rowClass={styles.tableRow}
          />
        </Box>
      );
    }
    return (
      <div className={styles.emptyWrapper}>
        <Icons.PlaceholderComplete className={styles.emptyImg} />
        <span className={styles.emptyMsg}>
          There are currently no considerations to display
        </span>
      </div>
    );
  };

  useEffect(() => {
    if (considerationsData.length === 0) {
      setShowSection(false);
    }
  }, [considerationsData.length]);

  return (
    <Box className={cx(styles.roundedSection, styles.considerationSection)}>
      <Box
        sx={sxStyles.headingWrapper}
        onClick={() => setShowSection(!showSection)}
      >
        <Typography sx={sxStyles.sectionHeading}>
          <BoltRoundedIcon
            sx={{
              color: Colors.white,
              backgroundColor: lightBlue[600],
              padding: "0.1rem",
              borderRadius: "0.25rem",
              marginRight: "0.5rem",
              fontSize: "1rem",
            }}
          />
          <span>Considerations</span>
        </Typography>
        <Box>
          {!showSection && (
            <ExpandMoreIcon
              sx={{
                color: Colors.grey_40,
              }}
            />
          )}
          {showSection && (
            <ExpandLessIcon
              sx={{
                color: Colors.grey_40,
              }}
            />
          )}
        </Box>
      </Box>
      <Collapse in={showSection} unmountOnExit>
        <SectionGrid>{renderConsiderationSection()}</SectionGrid>
      </Collapse>
      <NotificationToast
        message={ERROR_TOAST_MESSAGE}
        showToast={showErrorToast}
        isError
        autoHideDuration={2000}
        onClose={() => setShowErrorToast(false)}
      />
    </Box>
  );
};

export default ConsiderationSection;
